import { IComplexKeyValPair } from 'app/interfaces/IComplexKeyValPair';
import { IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { RestResourceDataset } from 'app/model/dataset/rest-resource.dataset';
import { DataFlowParameterConfig } from 'app/model/pipeline/data-flow-parameter-config';

import { BooleanDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { IntegerDataFlowType, StringDataFlowType } from '../dataflow-datatype';
import { isTruthy } from '../dataflow-expression-utilities';
import { DocumentForm } from '../dataflow-source';
import { DataFlowExpression } from '../dataflowexpression';
import { DataFlowDatasetSettings, DatasetFormatType } from './dataflow-dataset-settings';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

@Metamodel.model({
    name: adfModelTypeMap.rest.dataflowSettings
})
export class RestSettings extends DataFlowDatasetSettings {
    public requestMethod: DataFlowExpression;
    public requestBody: DataFlowExpression;
    public requestTimeout: DataFlowExpression;
    public requestInterval: DataFlowExpression;
    public requestEntity: DataFlowExpression;
    public additionalHeaders: DataFlowParameterConfig[];
    public paginationRules: DataFlowParameterConfig[];
    public queryParameters: DataFlowParameterConfig[];
    public insertHttpMethod: DataFlowExpression;
    public deleteHttpMethod: DataFlowExpression;
    public upsertHttpMethod: DataFlowExpression;
    public updateHttpMethod: DataFlowExpression;
    public httpCompressionType: DataFlowExpression;
    public batchSize: DataFlowExpression;
    public documentForm: DataFlowExpression;
    public namespaces: boolean;
    public namespacePrefixes: Array<IComplexKeyValPair<string, string>>;
    public nullValue: string;
    public restDataFormat: 'xml' | 'json';
    public dateFormat: string;
    public timestampFormat: string;
    @Metamodel.attribute({
        constraint: {
            validator: (owner: RestSettings, value: DataFlowExpression): IViolation[] => {
                const violations = [];
                if (Metamodel.modelOf(owner.owner).name === 'sink') {
                    if (isTruthy(owner.allowUpsert) || isTruthy(owner.allowUpdate) || isTruthy(owner.allowInsert) || isTruthy(owner.allowDelete)) {
                        if (isTruthy(owner.skipRowRelativeUrl) && (!value || !value.expression)) {
                            violations.push({
                                context: owner,
                                message: () => stringResources.DataFlowSkipRowRelativeUrlError
                            });
                        }
                        if (owner.restDataFormat && owner.batchSize && !owner.batchSize.templateExpression && owner.batchSize.expression && parseInt(owner.batchSize.expression, 10) > 1
                            && value && value.expression) {
                            violations.push({
                                context: owner,
                                message: () => stringResources.DataFlowBatchSizeRowRelativeUrlError
                            });
                        }
                    }
                }
                return violations;
            }
        }
    })
    public rowRelativeUrl: DataFlowExpression;
    public skipRowRelativeUrl: DataFlowExpression;

    public getDatasetType(): string {
        return Metamodel.modelFor(RestResourceDataset).name;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.Rest;
    }

    public getSubFormat(): string {
        return undefined;
    }

    public getIconClass(): string {
        return 'i-100x100-REST';
    }

    public initialize(subFormat: string): void {
        this.allowInsert = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'true');
        this.allowDelete = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.allowUpsert = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.allowUpdate = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.requestMethod = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'GET');
        this.requestBody = this.transform.createTemplatableExpression();
        this.requestTimeout = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE, '30');
        this.requestInterval = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE, '0');
        this.requestEntity = this.transform.createTemplatableExpression();
        this.additionalHeaders = [];
        this.paginationRules = [new DataFlowParameterConfig(this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'supportRFC5988'),
            this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'true'))];
        this.queryParameters = [];
        this.insertHttpMethod = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'POST');
        this.deleteHttpMethod = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'DELETE');
        this.upsertHttpMethod = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'PUT');
        this.updateHttpMethod = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, 'PATCH');
        this.httpCompressionType = this.transform.createTemplatableExpression();
        this.batchSize = this.transform.createTemplatableExpression(IntegerDataFlowType.TYPE);
        this.documentForm = this.transform.createTemplatableExpression(StringDataFlowType.TYPE, DocumentForm.documentPerLine);
        this.namespaces = false;
        this.namespacePrefixes = [];
        this.restDataFormat = 'json';
        this.rowRelativeUrl = this.transform.createTemplatableExpression();
        this.skipRowRelativeUrl = this.transform.createTemplatableExpression();
    }
}
